import sortBy from 'lodash/sortBy';

/**
 * Given a list of range objects of the form { from: Number, to: Number },
 * this function sorts those ranges by `from` and merges ranges that overlap
 **/
export const sortAndMergeRanges = (ranges = []) => {
  const sortedRanges = sortBy(ranges, range => range.from);

  const mergedRanges = sortedRanges.reduce((merged, range, ind) => {
    if (ind === 0) {
      return merged.concat(range);
    }

    const lastInd = merged.length - 1;
    const previousRange = merged[lastInd];

    if (previousRange == null || previousRange.to < range.from) {
      return merged.concat(range);
    }

    merged[lastInd] = {
      from: Math.min(previousRange.from, range.from),
      to: Math.max(previousRange.to, range.to),
    };

    return merged;
  }, []);

  return mergedRanges;
};

/**
 * Given a list of range objects of the form { from: Number, to: Number },
 * this function sorts those ranges by `from`, merges the ranges that overlap,
 * and then finds the compliment ranges within `minValue` and `maxValue`. These
 * complement ranges can have a padding with the original ranges provided so the
 * complements do not overlap at all.
 *
 * For more on complements:
 * https://en.wikipedia.org/wiki/Complement_(set_theory)
 **/
export const getComplementRanges = (
  ranges,
  minValue,
  maxValue,
  padding = 0
) => {
  const sortedAndMerged = sortAndMergeRanges(ranges);

  let currentStart = minValue;

  const complementRanges = [];

  sortedAndMerged.forEach(range => {
    // if there is a range before this one begins, add that as a complement
    if (currentStart < range.from) {
      complementRanges.push({
        from: Math.max(currentStart, minValue),
        to: Math.min(range.from, maxValue) - padding,
      });
    }

    currentStart = range.to + padding;
  });

  if (currentStart < maxValue) {
    complementRanges.push({
      from: currentStart,
      to: maxValue,
    });
  }

  return complementRanges;
};

export const areRangesDisjoint = (rangeA, rangeB) => {
  return rangeA.from >= rangeB.to || rangeB.from >= rangeA.to;
};

const doesRangeContain = (rangeA, rangeB) =>
  rangeA.from <= rangeB.from && rangeA.to >= rangeB.to;

// must return an array of ranges because a range may be split
export const getRangeDifference = (rangeA, rangeB) => {
  if (areRangesDisjoint(rangeA, rangeB)) {
    return [rangeA];
  }

  if (doesRangeContain(rangeA, rangeB)) {
    return [
      { from: rangeA.from, to: rangeB.from },
      { from: rangeB.to, to: rangeA.to },
    ];
  }

  if (doesRangeContain(rangeB, rangeA)) {
    return [];
  }

  if (rangeA.from <= rangeB.from) {
    return [{ from: rangeA.from, to: rangeB.from }];
  }

  return [
    {
      from: rangeB.to,
      to: rangeA.to,
    },
  ];
};

// This function takes an array of rangesA and removes all overlaps with rangesB
// from rangesA.
// Is there a way to do this that isn't O(n^2)?
export const removeRangeOverlaps = (rangesA, rangesB) => {
  let newRanges = [...rangesA];

  for (const removedRange of rangesB) {
    newRanges = newRanges.reduce(
      (acc, range) => acc.concat(getRangeDifference(range, removedRange)),
      []
    );
  }

  return newRanges;
};

export const isNumberInRanges = (ranges, number) => {
  for (const range of ranges) {
    if (range.from <= number && range.to >= number) {
      return true;
    }
  }

  return false;
};
